import { ReactNode } from 'react'
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { cn } from '@/lib/utils'

export interface TableColumn<T = unknown> {
  key: string
  label: string
  render?: (_item: T) => ReactNode
  className?: string
}

export interface TableAction<T = unknown> {
  icon?: ReactNode
  label: string
  onClick: (_item: T) => void
  className?: string
  variant?: 'default' | 'primary' | 'danger'
  show?: (_item: T) => boolean
}

interface GenericTableProps<T> {
  actions?: TableAction<T>[]
  className?: string
  columns: TableColumn<T>[]
  data: T[]
  emptyMessage?: string
  getRowKey: (_item: T) => string | number
  onDelete?: (_item: T) => void
  onEdit?: (_item: T) => void
  showDefaultActions?: boolean
}

const variantClasses = {
  default: 'text-gray-600 hover:text-gray-800',
  primary: 'text-blue-600 hover:text-blue-800',
  danger: 'text-red-600 hover:text-red-800',
}

export function GenericTable<T>({
  columns,
  data,
  actions = [],
  emptyMessage = 'No existen resultados',
  getRowKey,
  className,
  showDefaultActions = false,
  onEdit,
  onDelete,
}: GenericTableProps<T>) {
  const hasActions =
    actions.length > 0 || (showDefaultActions && (onEdit || onDelete))

  const defaultActions: TableAction<T>[] = []
  if (showDefaultActions) {
    if (onEdit) {
      defaultActions.push({
        icon: <PencilSquareIcon className="h-4 w-4" />,
        label: 'Editar',
        onClick: onEdit,
        variant: 'primary',
      })
    }
    if (onDelete) {
      defaultActions.push({
        icon: <TrashIcon className="h-4 w-4" />,
        label: 'Eliminar',
        onClick: onDelete,
        variant: 'danger',
      })
    }
  }

  const allActions = [...actions, ...defaultActions]

  return (
    <table className={cn('w-full bg-[#FAFAFA] text-left', className)}>
      <thead className="sticky top-0 z-10 border-b border-[#D4D4D4] bg-[#FAFAFA]">
        <tr>
          {columns.map((column) => (
            <th
              className={cn(
                'bg-[#FAFAFA] px-2 py-2 font-geist text-sm font-medium tracking-[0.005em] text-[#262626]',
                column.className
              )}
              key={column.key}
            >
              {column.label}
            </th>
          ))}
          {hasActions && (
            <th
              className={cn(
                'bg-[#FAFAFA] px-2 py-2 text-right',
                'font-geist text-sm font-medium tracking-[0.005em] text-[#262626]'
              )}
            >
              Acciones
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr>
            <td
              className="bg-[#FAFAFA] px-3 py-8 text-center font-geist text-base font-medium text-[#262626]"
              colSpan={columns.length + (hasActions ? 1 : 0)}
            >
              {emptyMessage}
            </td>
          </tr>
        ) : (
          data.map((item) => (
            <tr
              className={cn(
                'border-b border-[#D4D4D4] bg-[#FAFAFA] transition-colors hover:bg-[#F0F0F0]',
                onEdit && 'cursor-pointer'
              )}
              key={getRowKey(item)}
              onClick={() => onEdit?.(item)}
            >
              {columns.map((column) => (
                <td
                  className={cn(
                    'px-2 py-2 font-geist text-sm font-normal text-[#262626]',
                    column.className
                  )}
                  key={column.key}
                >
                  {column.render
                    ? column.render(item)
                    : String(
                        (item as Record<string, unknown>)[column.key] ?? '-'
                      )}
                </td>
              ))}
              {hasActions && (
                <td
                  className="border-b border-[#D4D4D4] px-3 py-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-end gap-2">
                    {allActions
                      .filter((action) =>
                        action.show ? action.show(item) : true
                      )
                      .map((action, idx) => (
                        <button
                          className={cn(
                            variantClasses[action.variant || 'default'],
                            action.className
                          )}
                          key={idx}
                          onClick={() => action.onClick(item)}
                          title={action.label}
                        >
                          {action.icon || action.label}
                        </button>
                      ))}
                  </div>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}
